import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import Fade from '@mui/material/Fade';
import TableSortLabel from '@mui/material/TableSortLabel';
import Divider from '@mui/material/Divider';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExportIcon from '@mui/icons-material/GetApp';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
import CsvIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { getStoredUser } from '../../../api/client';
import { getGroupsFromTimekeep, getStoresFromTimekeep } from '../../../api/timekeep';
import { createThirteenthMonthPayroll, 
         getThirteenthMonthByUser, 
         getThirteenthMonthSummaryTotal,
         getCompleteThirteenthMonth, 
         removeThirteenthMonth, 
         requestEdit13thMonthOtp, 
         revert13thMonthToProcessing, 
         verify13thMonthOtp, 
         getAllThirteenthMonthByUserCSV, 
         getAllThirteenthMonthByUser, 
         updateThirteenthMonthStatus } 
from '../../../api/payroll';
import { exportToPrint, formatDataForExport } from '../../../utils/exportUtils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import OtpInput from 'react-otp-input';

// Mock data for stores (this would come from API in production)
const ALL_STORES = [
  'SM Cebuano North',
  'SM Cebuano Seaside',
  'Jollibee Food 1',
  'Jollibee Food 2',
  'Sinjay Surfing Mega Mall R01',
  'JOLLIBEE BANAWE',
  'JOLLIBEE ORTIGAS',
  'JOLLIBEE MAKATI'
];

// Local storage key for persisting 13th month payroll records
const STORAGE_KEY_13TH = 'thirteenthMonthPayrollRecords';

// Helpers to persist and load saved records
const loadSaved13thMonthRecords = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_13TH);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const persist13thMonthRecords = (records) => {
  try {
    localStorage.setItem(STORAGE_KEY_13TH, JSON.stringify(records || []));
  } catch (_) {
    // ignore persistence errors
  }
};

// Mock data for existing 13th month payroll records
const generateMockPayrollRecords = () => {
  const periods = [
    { start: '2024-10-16', end: '2025-02-28' },
    { start: '2024-09-16', end: '2025-01-31' },
    { start: '2024-08-16', end: '2024-12-31' },
    { start: '2024-07-16', end: '2024-11-30' }
  ];

  const createdBy = ['Admin User', 'HR Manager', 'Payroll Staff', 'Super Admin'];

  return ALL_STORES.map((store, index) => {
    const period = periods[index % periods.length];
    const createdAt = dayjs()
      .subtract(Math.floor(Math.random() * 30), 'days')
      .format('YYYY-MM-DD HH:mm:ss');

    const isCompleted = Math.random() > 0.3;

    return {
      id: index + 1,
      store: store,
      periodCovered: `${dayjs(period.start).format('MMM DD, YYYY')} - ${dayjs(period.end).format('MMM DD, YYYY')}`,
      status: isCompleted ? 'completed' : 'processing',
      isEnabled: isCompleted ? (Math.random() > 0.5) : false, // Only completed items can be enabled
      createdBy: createdBy[index % createdBy.length],
      createdAt: createdAt,
      employeeCount: Math.floor(Math.random() * 50) + 10,
      totalAmount: Math.floor(Math.random() * 100000) + 50000
    };
  });
};

const ThirteenthMonthPay = () => {
  // State management for payroll records
  const [records, setRecords] = useState([]);
  const [stores, setStores] = useState(ALL_STORES);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [user, setUser] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0); // 0 for Processing, 1 for Saved

  // Filter state
  const [selectedStore, setSelectedStore] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(6, 'months'));
  const [toDate, setToDate] = useState(dayjs());

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null
  });

  // OTP Dialog state
  const [otpDialog, setOtpDialog] = useState({
    open: false,
    thirteenthMonthId: null,
    otp: '',
    requestedBy: '',
    loading: false,
    cooldownTime: 0,
    lastOtpRequest: null,
    step: 1 // 1: Requested By, 2: OTP Verification, 3: Revert Confirmation
  });

  const [editingThirteenthMonth, setEditingThirteenthMonth] = useState(null);
  const [revertingId, setRevertingId] = useState(null);

  // Enhanced table state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('store');

  // Theme and responsive
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Load user and filter stores based on permissions
  useEffect(() => {
    const loadUser = async () => {
      try {
        const storedUser = getStoredUser();
        setUser(storedUser);

        if (storedUser) {
          let availableStores = [];
          const response = await getStoresFromTimekeep(storedUser?.groupAccess);
          // Super admin gets all stores
          // if (storedUser.role === 'super-admin') {
          //   availableStores = response.data.map(store => ({
          //     value: store,
          //     label: store
          //   }));
          // } else {
          //   // Check for group access or store access
          //   const groupAccess = storedUser.groupAccess || [];
          //   const storeAccess = storedUser.storeAccess || [];

          //   // Filter stores based on access
          //   if (groupAccess.length > 0 || storeAccess.length > 0) {
          //     availableStores = ALL_STORES
          //       .filter(store => {
          //         // Check if store is in user's access list
          //         return storeAccess.includes(store) || 
          //                groupAccess.some(group => store.toLowerCase().includes(group.toLowerCase()));
          //       })
          //       .map(store => ({
          //         value: store,
          //         label: store
          //       }));
          //   }
          // }
          setStores(response.data);

          // If no stores available, show warning
          if (availableStores.length === 0) {
            setError('You do not have access to any stores. Please contact your administrator.');
          }
        }
      } catch (err) {
        console.error('Error loading user:', err);
        setError('Failed to load user permissions');
      }
    };

    loadUser();
  }, []);

  const fetchRecords = async () => {
    setTableLoading(true);
    setError(null);

    try {
      if (!user?.id && !user?._id) {
        throw new Error('User not loaded');
      }
      else {
        const query = {
          page,
          limit: rowsPerPage,
          search: searchTerm || undefined,
          from: fromDate ? fromDate.format('YYYY-MM-DD') : undefined,
          to: toDate ? toDate.format('YYYY-MM-DD') : undefined
        };

        const userId = user?.id || user._id;
        const res = await getThirteenthMonthByUser(userId, query);
        const backendList = res?.data || res?.records || [];
        const paginationOptions = res?.pagination || [];
        const normalized = await Promise.all((Array.isArray(backendList) ? backendList : []).map(async item => ({
          id: item._id || item.id,
          store: item.name,
          periodCovered: `${dayjs(item.from).format('MMM DD, YYYY')} - ${dayjs(item.to).format('MMM DD, YYYY')}`,
          status: item.status || 'processing',
          isEnabled: false,
          createdBy: item.createdBy,
          createdAt: item.createdAt,
          employeeCount: item.employeeCount || 0,
          totalAmount: item.status === 'completed' || item.status === 'display'
            ? await getThirteenthMonthSummaryTotal(item._id || item.id)
            : Number(item.total) || 0,
        })));
        setRecords(normalized);
        setTotalItems(res?.pagination?.totalItems || normalized.length);
        setDataLoaded(true);
        setLoading(false); // Set loading to false after data is loaded
        enqueueSnackbar('13th month payroll records loaded successfully', { variant: 'success' });
      }

      

    } catch (err) {
      setError('Failed to fetch 13th month payroll records');
      console.error('Error fetching records:', err);
      setLoading(false); // Set loading to false on error too
    } finally {
      setTableLoading(false);
    }
  };
  // Fetch 13th month payroll records when component mounts
  useEffect(() => {
    if (stores.length > 0) {
      fetchRecords();
    }
  }, [stores]);

  useEffect(() => {
    fetchRecords();
  }, [page]);

  // Countdown timer effect for OTP cooldown
  useEffect(() => {
    let interval;
    if (otpDialog.cooldownTime > 0) {
      interval = setInterval(() => {
        setOtpDialog(prev => {
          const newCooldownTime = Math.max(0, prev.cooldownTime - 1);
          if (newCooldownTime === 0) {
            clearStoredCooldown();
          }
          return { ...prev, cooldownTime: newCooldownTime };
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpDialog.cooldownTime]);

  // Initialize cooldown on component mount
  useEffect(() => {
    const storedCooldown = getStoredCooldown();
    if (storedCooldown.cooldownTime > 0) {
      setOtpDialog(prev => ({
        ...prev,
        cooldownTime: storedCooldown.cooldownTime,
        lastOtpRequest: storedCooldown.lastOtpRequest
      }));
    }
  }, []);

  // Auto-request OTP when moving to step 2
  useEffect(() => {
    if (otpDialog.open && otpDialog.thirteenthMonthId && otpDialog.step === 2) {
      requestOtp();
    }
  }, [otpDialog.open, otpDialog.thirteenthMonthId, otpDialog.step]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchRecords();
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error refreshing records:', err);
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  // Handle store click to navigate to view page
  const handleStoreClick = (record) => {
    navigate(`/payroll/13thmonth/view/${record.id}`, {
      state: { record }
    });
  };

  // Handle generate new payroll records
  const handleGenerate = async () => {
    if (!selectedStore) {
      enqueueSnackbar('Please select a store', { variant: 'warning' });
      return;
    }

    if (!fromDate || !toDate) {
      enqueueSnackbar('Please select both from and to dates', { variant: 'warning' });
      return;
    }

    if (fromDate.isAfter(toDate)) {
      enqueueSnackbar('From date cannot be after To date', { variant: 'warning' });
      return;
    }

    setTableLoading(true);
    try {
      const payload = {
        name: selectedStore,
        from: fromDate.format('YYYY-MM-DD'),
        to: toDate.format('YYYY-MM-DD'),
        createdBy: user?.fullName || 'System',
        uid: user?.id || 'System'
      };

      const response = await createThirteenthMonthPayroll(payload);

      // Expect backend to return the created record; fall back to shaping if needed
      const created = response?.data || response || {};

      const newRecord = {
        id: created.id || created._id || Date.now(),
        store: created.store || selectedStore,
        periodCovered: created.periodCovered || `${fromDate.format('MMM DD, YYYY')} - ${toDate.format('MMM DD, YYYY')}`,
        status: created.status || 'completed',
        isEnabled: created.isEnabled ?? false,
        createdBy: created.createdBy || (user ? user.name || 'Current User' : 'System'),
        createdAt: created.createdAt || dayjs().format('YYYY-MM-DD HH:mm:ss'),
        employeeCount: created.employeeCount || Math.floor(Math.random() * 50) + 10,
        totalAmount: created.totalAmount || 0
      };

      // Update UI
      fetchRecords()
      // setRecords(prev => [newRecord, ...prev]);
      setTotalItems(prev => prev + 1);
      enqueueSnackbar('New 13th month payroll record saved to database', { variant: 'success' });
    } catch (err) {
      console.error('Error saving 13th month payroll:', err);
      const message = err?.response?.data?.message || 'Failed to save 13th month payroll';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setTableLoading(false);
    }
  };

  // Handle save (change status from processing to completed)
  const handleSave = async (record) => {
    const data = {
      id: record.id
    }
    const result = await getCompleteThirteenthMonth(data)
    setRecords(prevRecords =>
      prevRecords.map(r =>
        r.id === record.id
          ? { ...r, status: 'completed', isEnabled: false }
          : r
      )
    );
    enqueueSnackbar(`Payroll record for ${record.store} has been saved`, { variant: 'success' });
  };

  // Handle toggle payslip with confirmation
  const handleTogglePayslip = async (record) => {

    const newStatus = !record.isEnabled;
    const result = await updateThirteenthMonthStatus(record.id)
    if(result.status === "success") {
      setConfirmDialog({
        open: true,
        title: `${newStatus ? 'Enable' : 'Disable'} Payslip`,
        message: `Are you sure you want to ${newStatus ? 'enable' : 'disable'} the payslip for ${record.store}?`,
        action: () => handleConfirmTogglePayslip(record, newStatus)
      });  

    }

    else {
      alert("Something went wrong please try again later!")
    }
    
  };

  // Handle confirm toggle payslip
  const handleConfirmTogglePayslip = async (record, newStatus) => {
    setRecords(prevRecords =>
      prevRecords.map(r =>
        r.id === record.id
          ? { ...r, isEnabled: newStatus }
          : r
      )
    );
    setConfirmDialog({ open: false, title: '', message: '', action: null });
    await fetchRecords();
    enqueueSnackbar(
      `Payslip ${newStatus ? 'enabled' : 'disabled'} for ${record.store}`,
      { variant: 'success' }
    );
  };

  // OTP cooldown utility functions
  const getStoredCooldown = () => {
    const stored = localStorage.getItem('otp_cooldown_13th');
    if (stored) {
      const { lastRequest, thirteenthMonthId } = JSON.parse(stored);
      const timeElapsed = Math.floor((Date.now() - lastRequest) / 1000);
      const remainingTime = Math.max(0, 60 - timeElapsed); // 60 second cooldown

      return {
        lastOtpRequest: lastRequest,
        cooldownTime: remainingTime,
        thirteenthMonthId: thirteenthMonthId
      };
    }
    return { lastOtpRequest: null, cooldownTime: 0, thirteenthMonthId: null };
  };

  const setStoredCooldown = (thirteenthMonthId) => {
    const cooldownData = {
      lastRequest: Date.now(),
      thirteenthMonthId: thirteenthMonthId
    };
    localStorage.setItem('otp_cooldown_13th', JSON.stringify(cooldownData));
  };

  const clearStoredCooldown = () => {
    localStorage.removeItem('otp_cooldown_13th');
  };

  // Handle edit payroll - open OTP dialog
  const handleEditPayroll = (record) => {
    const storedCooldown = getStoredCooldown();

    setOtpDialog({
      open: true,
      thirteenthMonthId: record.id,
      otp: '',
      requestedBy: '',
      loading: false,
      cooldownTime: storedCooldown.thirteenthMonthId === record.id ? storedCooldown.cooldownTime : 0,
      lastOtpRequest: storedCooldown.thirteenthMonthId === record.id ? storedCooldown.lastOtpRequest : null,
      step: 1
    });
    setEditingThirteenthMonth(record);
  };

  // Handle OTP dialog close
  const handleOtpClose = () => {
    clearStoredCooldown();
    setOtpDialog({
      open: false,
      thirteenthMonthId: null,
      otp: '',
      requestedBy: '',
      loading: false,
      cooldownTime: 0,
      lastOtpRequest: null,
      step: 1
    });
    setEditingThirteenthMonth(null);
  };

  // Handle OTP input change
  const handleOtpChange = (value) => {
    setOtpDialog(prev => ({ ...prev, otp: value }));
  };

  // Handle requested by change
  const handleRequestedByChange = (event) => {
    setOtpDialog(prev => ({ ...prev, requestedBy: event.target.value }));
  };

  // Handle proceed to OTP step
  const handleProceedToOtp = () => {
    if (!otpDialog.requestedBy.trim()) {
      enqueueSnackbar('Please enter who requested this edit', { variant: 'warning' });
      return;
    }
    setOtpDialog(prev => ({ ...prev, step: 2 }));
  };

  // Handle OTP submit
  const handleOtpSubmit = async () => {
    if (otpDialog.otp.length !== 6) {
      enqueueSnackbar('Please enter the complete 6-digit OTP', { variant: 'warning' });
      return;
    }

    if (!otpDialog.requestedBy.trim()) {
      enqueueSnackbar('Please enter who requested this edit', { variant: 'warning' });
      return;
    }

    setOtpDialog(prev => ({ ...prev, loading: true }));

    try {
      // Verify OTP with backend
      const response = await verify13thMonthOtp(otpDialog.otp);

      if (response.status === 'success') {
        clearStoredCooldown();
        enqueueSnackbar('OTP verified successfully', { variant: 'success' });
        setOtpDialog(prev => ({ ...prev, loading: false, cooldownTime: 0, lastOtpRequest: null, step: 3 }));
      } else {
        enqueueSnackbar('Invalid OTP. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      enqueueSnackbar('Failed to verify OTP. Please try again.', { variant: 'error' });
    } finally {
      setOtpDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Request OTP
  const requestOtp = async () => {
    if (!otpDialog.thirteenthMonthId || otpDialog.cooldownTime > 0) return;

    try {
      const response = await requestEdit13thMonthOtp(otpDialog.thirteenthMonthId);

      if (response.status === 'success') {
        setStoredCooldown(otpDialog.thirteenthMonthId);
        setOtpDialog(prev => ({
          ...prev,
          cooldownTime: 60, // 1 minute cooldown
          lastOtpRequest: Date.now()
        }));
        enqueueSnackbar('OTP sent to your registered email', { variant: 'info' });
      } else {
        enqueueSnackbar('Failed to send OTP. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('OTP request error:', error);
      enqueueSnackbar('Failed to send OTP. Please try again.', { variant: 'error' });
    }
  };

  // Handle revert to processing
  const handleRevertToProcessing = async () => {
    if (!editingThirteenthMonth) return;

    try {
      setRevertingId(editingThirteenthMonth.id);

      const result = await revert13thMonthToProcessing(editingThirteenthMonth.id);

      if (result.status === 'success') {
        enqueueSnackbar('13th month payroll reverted to processing successfully. You can now edit this payroll.', { variant: 'success' });
        // Close the dialog and refresh the data
        handleOtpClose();
        await fetchRecords();
      } else {
        enqueueSnackbar(result.message || 'Failed to revert payroll to processing', { variant: 'error' });
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || 'Failed to revert payroll to processing';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setRevertingId(null);
      setEditingThirteenthMonth(null);
    }
  };

  const handleCancelEdit = () => {
    setConfirmDialog({ open: false, title: '', message: '', action: null });
  };

  // Handle delete payroll - open confirmation dialog
  const handleDeletePayroll = (record) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Payroll Record',
      message: `Are you sure you want to delete the payroll record for ${record.store}? This action cannot be undone.`,
      action: () => handleConfirmDelete(record)
    });
  };

  // Handle confirm delete
  const handleConfirmDelete = async (record) => {
    const data = {
      id: record.id
    }
    const result = await removeThirteenthMonth(data)
    if (result.status === "success") {
      setRecords(prevRecords => prevRecords.filter(r => r.id !== record.id));
      setConfirmDialog({ open: false, title: '', message: '', action: null });
      enqueueSnackbar(`Payroll record for ${record.store} has been deleted`, { variant: 'success' });
    }
    else {
      alert("Something went wrong please try again later")
    }

  };

  // Sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(page);
  };

  // Search filtering
  const filteredRecords = useMemo(() => {
    let filtered = [...records];

    // Filter by tab (status)
    if (activeTab === 0) {
      filtered = filtered.filter(record => record.status === 'processing');
    } else {
      filtered = filtered.filter(record => record.status === 'completed' || record.status === "display");
    }

    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.store.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.periodCovered.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.createdBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.status.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return filtered;
  }, [records, searchTerm, order, orderBy, activeTab]);

  // Pagination
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const paginatedRecords = useMemo(() => {


    const startIndex = (page - 1) * rowsPerPage;
    return filteredRecords.slice(startIndex, startIndex + rowsPerPage);


  }, [filteredRecords, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(1);
  };

  // Handle search
  const handleSearch = () => {
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  // Export handlers
  const handleExportCSV = () => {
    if (!filteredRecords || filteredRecords.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    // Transform data for CSV export
    const csvData = filteredRecords.map(record => ({
      'Store': record.store,
      'Period Covered': record.periodCovered,
      'Status': record.status,
      'Created By': record.createdBy,
      'Created At': record.createdAt,
      'Employee Count': record.employeeCount,
      'Total Amount': record.totalAmount.toFixed(2)
    }));

    // Extract headers
    const headers = Object.keys(csvData[0]);

    // Build CSV rows
    const csvRows = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(field => JSON.stringify(row[field] ?? '')).join(',')
      )
    ];

    // Prepend BOM for Excel support
    const csvContent = '\uFEFF' + csvRows.join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `13th_month_payroll_records_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    enqueueSnackbar('CSV exported successfully', { variant: 'success' });
  };

  const handlePrintCSVAll = async () => {
    enqueueSnackbar('Preparing for downloading. This may take a moment...', { variant: 'info' });
    try {

      if (!user?.id && !user?._id)
        throw new Error('User not loaded');

      const userId = user?.id || user._id;

      const query = {
        status: activeTab === 0 ? 'processing' : 'completed',
      }

      // Process and download the CSV 
      const allRecordsResponse = await getAllThirteenthMonthByUserCSV(userId, query);
      console.log('all records response', allRecordsResponse)
      let url = URL.createObjectURL(allRecordsResponse)
      window.open(url)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error preparing data for print all:', err);
      enqueueSnackbar('Failed to prepare data for downloading', { variant: 'error' });
    }
  }

  const handleExportPDF = () => {
    if (!filteredRecords || filteredRecords.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Add title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('13th Month Payroll Records', 14, 20);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${dayjs().format('MM/DD/YYYY hh:mm A')}`, 14, 28);

      // Prepare table data
      const tableData = filteredRecords.map(record => [
        record.store,
        record.periodCovered,
        record.status,
        record.createdBy,
        dayjs(record.createdAt).format('MM/DD/YYYY'),
        record.employeeCount.toString(),
        `₱${record.totalAmount.toFixed(2)}`
      ]);

      doc.autoTable({
        head: [[
          'Store',
          'Period Covered',
          'Status',
          'Created By',
          'Created At',
          'Employees',
          'Total Amount'
        ]],
        body: tableData,
        startY: 35,
        styles: {
          fontSize: 9,
          cellPadding: 2,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [18, 193, 220],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center'
        },
        columnStyles: {
          0: { cellWidth: 40, halign: 'left' },
          1: { cellWidth: 35, halign: 'left' },
          2: { cellWidth: 20, halign: 'center' },
          3: { cellWidth: 25, halign: 'left' },
          4: { cellWidth: 20, halign: 'center' },
          5: { cellWidth: 20, halign: 'center' },
          6: { cellWidth: 30, halign: 'right' }
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250]
        }
      });

      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
      }

      doc.save(`13th_month_payroll_records_${dayjs().format('YYYY-MM-DD')}.pdf`);
      enqueueSnackbar('PDF exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('PDF export error:', error);
      enqueueSnackbar('Failed to export PDF', { variant: 'error' });
    }
  };

  const handlePrint = () => {
    if (!filteredRecords || filteredRecords.length === 0) {
      enqueueSnackbar('No data to print', { variant: 'warning' });
      return;
    }

    // Transform data for print
    const printData = filteredRecords.map(record => ({
      'Store': record.store,
      'Period Covered': record.periodCovered,
      'Status': record.status,
      'Created By': record.createdBy,
      'Created At': dayjs(record.createdAt).format('MM/DD/YYYY'),
      'Employee Count': record.employeeCount,
      'Total Amount': `₱${record.totalAmount.toFixed(2)}`
    }));

    const formattedData = formatDataForExport(printData, '13th_month_payroll');
    exportToPrint(formattedData, `13th Month Payroll Records - ${dayjs().format('MM/DD/YYYY')}`);
  };

  const handlePrintAll = async () => {
    enqueueSnackbar('Preparing for printing. This may take a moment...', { variant: 'info' });
    try {
      if (!user?.id && !user?._id) {
        throw new Error('User not loaded');
      }
      const userId = user?.id || user._id;
      const query = {
        status: activeTab === 0 ? 'processing' : 'completed',
      }
      const allRecordsResponse = await getAllThirteenthMonthByUser(userId, query);
      const backendList = allRecordsResponse.filter((response) => response) || [];

      // Normalize to table shape
      const normalizedData = backendList.map(item => ({
        'Store': item.name,
        'Period Covered': `${dayjs(item.from).format('MMM DD, YYYY')} - ${dayjs(item.to).format('MMM DD, YYYY')}`,
        'Status': item.status,
        'Created By': item.createdBy,
        'Created At': dayjs(item.createdAt).format('MM/DD/YYYY'),
        'Total Amount': `₱${(item.totalAmount || 0).toFixed(2)}`
      }));


      const formattedData = formatDataForExport(normalizedData, '13th_month_payroll');
      exportToPrint(formattedData, `13th Month Payroll Records - ${dayjs().format('MM/DD/YYYY')}`);
    } catch (err) {
      console.error('Error preparing data for print all:', err);
      enqueueSnackbar('Failed to prepare data for printing', { variant: 'error' });
    }
  }


  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="200px" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="300px" height={20} sx={{ mb: 3 }} />
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width="200px" height={36} />
            <Skeleton variant="rectangular" width="120px" height={36} />
          </Box>
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="100%" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ReceiptIcon sx={{ color: '#12C1DC' }} />
              13th Month Payroll
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing || !dataLoaded}
                sx={{
                  color: '#12C1DC',
                  '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} sx={{ color: '#12C1DC' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/payroll">
              Payroll
            </Link>
            <Typography color="text.primary">13th Month Payroll</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          </motion.div>
        )}

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={stores.map(store => ({ value: store, label: store }))}
                    getOptionLabel={(option) => option.label || ''}
                    value={stores.find(store => store.value === selectedStore) ? { value: selectedStore, label: selectedStore } : null}
                    onChange={(event, newValue) => {
                      setSelectedStore(newValue ? newValue.value : '');
                      setPage(1);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Store"
                        placeholder="Type to search stores..."
                      />
                    )}
                    clearOnEscape
                    autoHighlight
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={(newValue) => {
                      setFromDate(newValue);
                      setPage(1);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'medium'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={(newValue) => {
                      setToDate(newValue);
                      setPage(1);
                    }}
                    minDate={fromDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'medium'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleGenerate}
                    disabled={tableLoading || !selectedStore || !fromDate || !toDate}
                    sx={{
                      height: '56px',
                      bgcolor: '#12C1DC',
                      '&:hover': { bgcolor: '#0FABDB' }
                    }}
                  >
                    {tableLoading ? <CircularProgress size={24} /> : 'Generate'}
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="medium"
                    placeholder="Search by store, period, status, or created by..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#9ca3af' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchTerm('');
                              setPage(1);
                            }}
                            sx={{ color: '#9ca3af' }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: '#12C1DC' },
                        '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Paper>
        </motion.div>

        {/* Tabs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper sx={{ borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  minHeight: 64,
                  '&.Mui-selected': {
                    color: '#12C1DC',
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#12C1DC',
                  height: 3,
                }
              }}
            >
              <Tab
                label={`Processing (${records.filter(r => r.status === 'processing').length})`}
                sx={{ flex: 1 }}
              />
              <Tab
                label={`Saved (${records.filter(r => r.status === 'completed' || r.status === "display").length})`}
                sx={{ flex: 1 }}
              />
            </Tabs>
          </Paper>
        </motion.div>

        {/* Table Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Table Controls */}
            <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Show
                  </Typography>
                  <TextField
                    select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    size="small"
                    sx={{ minWidth: 80 }}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </TextField>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                    entries
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {/* Export Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>



                    <Tooltip title="Export CSV">
                      <IconButton
                        size="small"
                        onClick={handlePrintCSVAll}
                        disabled={!dataLoaded || filteredRecords.length === 0}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                        }}
                      >
                        <CsvIcon fontSize="small" sx={{ color: '#6b7280' }} />
                      </IconButton>
                    </Tooltip>



                    <Tooltip title="Print">
                      <IconButton
                        size="small"
                        onClick={handlePrintAll}
                        disabled={!dataLoaded || filteredRecords.length === 0}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                        }}
                      >
                        <PrintIcon fontSize="small" sx={{ color: '#6b7280' }} />
                      </IconButton>
                    </Tooltip>


                    {/*

                    <Tooltip title="Print">
                      <IconButton
                        size="small"
                        onClick={handlePrintAll}
                        disabled={!dataLoaded || filteredRecords.length === 0}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                        }}
                      >
                        <PrintIcon fontSize="small" sx={{ color: '#6b7280' }} />
                      </IconButton>
                    </Tooltip>*/}

                    {/*
                    <Tooltip title="Export PDF">
                      <IconButton
                        size="small"
                        onClick={handleExportPDF}
                        disabled={!dataLoaded || filteredRecords.length === 0}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                        }}
                      >
                        <PdfIcon fontSize="small" sx={{ color: '#6b7280' }} />
                      </IconButton>
                    </Tooltip>
                    */}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Enhanced Table */}
            <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto', position: 'relative' }}>
              {tableLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: 1
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} sx={{ color: '#12C1DC' }} />
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        Loading records...
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              )}
              <Table
                stickyHeader
                size={isMobile ? "small" : "medium"}
                aria-label="13th month pay table"
              >
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-root': {
                      backgroundColor: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }
                  }}>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Actions
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === 'store' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '200px'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'store'}
                        direction={orderBy === 'store' ? order : 'asc'}
                        onClick={() => handleRequestSort('store')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Store
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === 'periodCovered' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '180px'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'periodCovered'}
                        direction={orderBy === 'periodCovered' ? order : 'asc'}
                        onClick={() => handleRequestSort('periodCovered')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Period Covered
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === 'status' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '100px'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'status'}
                        direction={orderBy === 'status' ? order : 'asc'}
                        onClick={() => handleRequestSort('status')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === 'createdBy' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '150px'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'createdBy'}
                        direction={orderBy === 'createdBy' ? order : 'asc'}
                        onClick={() => handleRequestSort('createdBy')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Created By
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === 'createdAt' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '120px'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'createdAt'}
                        direction={orderBy === 'createdAt' ? order : 'asc'}
                        onClick={() => handleRequestSort('createdAt')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Created At
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align="right"
                      sortDirection={orderBy === 'totalAmount' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '150px'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'totalAmount'}
                        direction={orderBy === 'totalAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('totalAmount')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Total 13th MP
                      </TableSortLabel>
                    </TableCell>
                    {activeTab === 1 && (
                      <TableCell sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '100px'
                      }}>
                        Payslip Status
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!dataLoaded && records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={activeTab === 1 ? 8 : 7} sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                          Loading Records...
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                          Please wait while we fetch the 13th month payroll records.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={activeTab === 1 ? 8 : 7} sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                          No Records Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                          {searchTerm ? 'Try adjusting your search terms.' : 'No payroll records available for this tab.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <AnimatePresence>
                      {filteredRecords.map((record, index) => {
                        const isEvenRow = index % 2 === 0;
                        return (
                          <motion.tr
                            key={record.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                            style={{
                              display: 'table-row',
                              backgroundColor: isEvenRow ? '#ffffff' : '#f8f9fa'
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {record.status === 'processing' ? (
                                  <>
                                    <Tooltip title="Save payroll record">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleSave(record)}
                                        sx={{
                                          color: '#10b981',
                                          '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' }
                                        }}
                                      >
                                        <CheckCircleIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete payroll record">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeletePayroll(record)}
                                        sx={{
                                          color: '#ef4444',
                                          '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : (
                                  <>
                                    <Tooltip title={record.status === "display" ? "Disable payslip" : "Enable payslip"}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleTogglePayslip(record)}
                                        sx={{
                                          color: record.status === "display" ? '#ef4444' : '#10b981',
                                          '&:hover': {
                                            bgcolor: record.status === "display" ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)'
                                          }
                                        }}
                                      >
                                        {record.status === "display" ? (
                                          <CancelIcon fontSize="small" />
                                        ) : (
                                          <CheckCircleIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit payroll">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditPayroll(record)}
                                        sx={{
                                          color: '#f59e0b',
                                          '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.1)' }
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {/*<Tooltip title="Delete payroll record">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeletePayroll(record)}
                                        sx={{
                                          color: '#ef4444',
                                          '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>*/}
                                  </>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReceiptIcon sx={{ color: '#12C1DC', fontSize: '1.2rem' }} />
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  onClick={() => handleStoreClick(record)}
                                  sx={{
                                    cursor: 'pointer',
                                    color: '#12C1DC',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                      color: '#0FABDB'
                                    }
                                  }}
                                >
                                  {record.store}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {record.periodCovered}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={record.status}
                                size="small"
                                sx={{
                                  bgcolor: record.status === 'completed' || record.status === "display" ? '#e8f5e9' : '#fff3e0',
                                  color: record.status === 'completed' || record.status === "display" ? '#2e7d32' : '#f57c00',
                                  fontWeight: 600
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {record.createdBy}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {dayjs(record.createdAt).format('MMM DD, YYYY')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {dayjs(record.createdAt).format('hh:mm A')}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: 'black' }}>
                              ₱
                              {(record.totalAmount || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            {activeTab === 1 && (
                              <TableCell>
                                <Chip
                                  label={record.isEnabled ? 'Enabled' : 'Disabled'}
                                  size="small"
                                  sx={{
                                    bgcolor: record.isEnabled ? '#e8f5e9' : '#ffebee',
                                    color: record.isEnabled ? '#2e7d32' : '#c62828',
                                    fontWeight: 600
                                  }}
                                />
                              </TableCell>
                            )}
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination - Always visible */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              gap: { xs: 2, sm: 0 }
            }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                {dataLoaded && filteredRecords.length > 0 ? (
                  <>
                    Showing  {totalItems} payroll record{filteredRecords.length !== 1 ? 's' : ''}
                    {searchTerm && ` (filtered)`}
                  </>
                ) : (
                  'No records to display'
                )}
              </Typography>
              <Pagination
                count={totalPages || 1}
                page={page}
                onChange={handlePageChange}
                size={isMobile ? "small" : "medium"}
                disabled={!dataLoaded || totalItems === 0}    
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      backgroundColor: '#12C1DC',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0FABDB'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </motion.div>

        {/* Confirmation Dialog for Toggle Payslip and Delete */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCancelEdit}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle sx={{
            textAlign: 'center',
            pb: 2,
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white'
          }}>
            <Typography variant="h6" fontWeight="bold">
              {confirmDialog.title}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 2 }}>
            <DialogContentText sx={{ textAlign: 'center', fontSize: '1rem', color: '#374151' }}>
              {confirmDialog.message}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0, gap: 2, justifyContent: 'center' }}>
            <Button
              onClick={handleCancelEdit}
              variant="outlined"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDialog.action}
              variant="contained"
              sx={{
                bgcolor: '#f59e0b',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#d97706' }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* OTP Verification Dialog */}
        <Dialog
          open={otpDialog.open}
          onClose={handleOtpClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle sx={{
            textAlign: 'center',
            pb: 2,
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white'
          }}>
            <Typography variant="h6" fontWeight="bold">
              {otpDialog.step === 1 ? 'Proceed to Edit Payroll' :
                otpDialog.step === 2 ? 'Verify OTP to Edit Payroll' :
                  'Confirm Edit Payroll'}
            </Typography>
            {otpDialog.step === 2 && otpDialog.cooldownTime > 0 && (
              <Typography variant="caption" sx={{ mt: 1, opacity: 0.8, display: 'block' }}>
                Next OTP available in {otpDialog.cooldownTime}s
              </Typography>
            )}

            {/* Step Indicator */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: otpDialog.step >= 1 ? 'white' : 'rgba(255,255,255,0.3)'
              }} />
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: otpDialog.step >= 2 ? 'white' : 'rgba(255,255,255,0.3)'
              }} />
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: otpDialog.step >= 3 ? 'white' : 'rgba(255,255,255,0.3)'
              }} />
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {/* Step 1: Requested By */}
            {otpDialog.step === 1 && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 3, mt: 2, color: '#374151', fontWeight: 600 }}>
                  Proceed to editing this 13th month payroll?
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Requested By"
                    variant="outlined"
                    value={otpDialog.requestedBy}
                    onChange={handleRequestedByChange}
                    placeholder="Enter who requested this edit"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: '#f59e0b' },
                        '&.Mui-focused fieldset': { borderColor: '#f59e0b' }
                      }
                    }}
                  />
                </Box>

                {editingThirteenthMonth && (
                  <Box sx={{
                    bgcolor: '#f8f9fa',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #e5e7eb',
                    textAlign: 'left'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      13th Month Payroll Details:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Store:</strong> {editingThirteenthMonth.store}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Period:</strong> {editingThirteenthMonth.periodCovered}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Total Amount:</strong> ₱{(editingThirteenthMonth.totalAmount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created By:</strong> {editingThirteenthMonth.createdBy}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Step 2: OTP Verification */}
            {otpDialog.step === 2 && (
              <>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body1" sx={{ mb: 2, color: '#6b7280' }}>
                    Enter the 6-digit code sent to your email
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <OtpInput
                      value={otpDialog.otp}
                      onChange={handleOtpChange}
                      numInputs={6}
                      separator={<span style={{ width: '8px' }}></span>}
                      inputType="tel"
                      shouldAutoFocus={true}
                      renderInput={(props, index) => (
                        <input
                          {...props}
                          key={index}
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            margin: '0 0.25rem',
                            fontSize: '1.25rem',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            border: otpDialog.otp.length === 6 ? '2px solid #10b981' : '2px solid #d1d5db',
                            textAlign: 'center',
                            outline: 'none',
                            transition: 'all 0.2s ease-in-out',
                            backgroundColor: otpDialog.otp.length === 6 ? '#f0fdf4' : '#ffffff'
                          }}
                          onFocus={(e) => {
                            e.target.style.border = '2px solid #f59e0b';
                            e.target.style.outline = 'none';
                          }}
                          onBlur={(e) => {
                            e.target.style.border = otpDialog.otp.length === 6 ? '2px solid #10b981' : '2px solid #d1d5db';
                          }}
                        />
                      )}
                    />
                  </Box>
                  {otpDialog.otp.length === 6 && (
                    <Typography variant="caption" sx={{ mt: 1, color: '#10b981', fontWeight: 500 }}>
                      ✓ All digits entered
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="text"
                    onClick={requestOtp}
                    disabled={otpDialog.loading || otpDialog.cooldownTime > 0}
                    sx={{
                      color: otpDialog.cooldownTime > 0 ? '#9ca3af' : '#f59e0b',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: otpDialog.cooldownTime > 0 ? 'transparent' : 'rgba(245, 158, 11, 0.1)'
                      },
                      '&:disabled': {
                        color: '#9ca3af',
                        cursor: 'not-allowed'
                      }
                    }}
                  >
                    {otpDialog.cooldownTime > 0
                      ? `Resend OTP in ${otpDialog.cooldownTime}s`
                      : "Didn't receive OTP? Resend"
                    }
                  </Button>
                </Box>
              </>
            )}

            {/* Step 3: Revert Confirmation */}
            {otpDialog.step === 3 && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 3, mt: 2, color: '#374151', fontWeight: 600 }}>
                  Edit Saved 13th Month Payroll
                </Typography>

                <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
                  Saved payrolls cannot be edited directly. To make changes, you need to revert this payroll back to processing status.
                </Typography>

                {editingThirteenthMonth && (
                  <Box sx={{
                    bgcolor: '#f8f9fa',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #e5e7eb',
                    textAlign: 'left',
                    mb: 3
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      13th Month Payroll Details:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Store:</strong> {editingThirteenthMonth.store}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Period:</strong> {editingThirteenthMonth.periodCovered}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Total Amount:</strong> ₱{(editingThirteenthMonth.totalAmount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created By:</strong> {editingThirteenthMonth.createdBy}
                    </Typography>
                  </Box>
                )}

                <Typography variant="body1" sx={{ color: '#374151', fontWeight: 500 }}>
                  Are you sure you want to revert this payroll to processing status and continue with editing?
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, gap: 2, justifyContent: 'center' }}>
            <Button
              onClick={
                otpDialog.step === 2 ? () => setOtpDialog(prev => ({ ...prev, step: 1 })) :
                  otpDialog.step === 3 ? () => setOtpDialog(prev => ({ ...prev, step: 2 })) :
                    handleOtpClose
              }
              variant="outlined"
              disabled={otpDialog.loading || (otpDialog.step === 3 && revertingId === editingThirteenthMonth?.id)}
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              {otpDialog.step === 1 ? 'Cancel' :
                otpDialog.step === 2 ? 'Back' :
                  'Back'}
            </Button>

            <Button
              onClick={
                otpDialog.step === 1 ? handleProceedToOtp :
                  otpDialog.step === 2 ? handleOtpSubmit :
                    handleRevertToProcessing
              }
              variant="contained"
              disabled={
                otpDialog.step === 1 ? !otpDialog.requestedBy.trim() :
                  otpDialog.step === 2 ? otpDialog.loading || otpDialog.otp.length !== 6 :
                    revertingId === editingThirteenthMonth?.id
              }
              sx={{
                bgcolor: '#f59e0b',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#d97706' },
                '&:disabled': {
                  bgcolor: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              {otpDialog.step === 1 ? 'Proceed' :
                otpDialog.step === 2 ? (
                  otpDialog.loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      Verifying...
                    </>
                  ) : 'Verify'
                ) :
                  revertingId === editingThirteenthMonth?.id ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      Reverting...
                    </>
                  ) : (
                    'Revert to Processing & Edit'
                  )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default ThirteenthMonthPay;
